import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'pilot',
    emoji: '✈️',
    title: '파일럿',
    category: '항공·교통',
    color: 'from-blue-500 to-sky-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
    story: `중학교 1학년 지수(13살)는 미래의 민항기 조종사를 꿈꿉니다.
비행 시뮬레이터를 하다가 문득 궁금해졌어요.
"고도 +3,000m와 -500m는 어떻게 비교하지? 해수면이 0이면…"
파일럿 삼촌이 말했습니다: "양수·음수만 알면 고도·속도 다 표현할 수 있어!"`,
    situation: '고도: 해수면(0m) 기준. 3,000m 상공 = +3,000, 해저 500m = -500.',
    question: '고도 +3,000m에서 -500m로 내려갈 때 고도 변화는? 두 고도의 차이는?',
    xLabel: '시간(분)',
    yLabel: '고도(m)',
    slope: -350,
    intercept: 3000,
    slopeDesc: '분당 -350m 하강 (음수 = 감소)',
    interceptDesc: '출발 고도 +3,000m (양수)',
    equation: '고도 = -350t + 3000',
    answer: '변화량: -500 - 3000 = -3,500m (하강)\n두 고도 차이: |3000 - (-500)| = 3,500m',
    whyIntegers: '양수는 해수면 위, 음수는 해수면 아래. 정수로 표현하면 방향까지 포함!',
    insight: '파일럿, 잠수함 승무원, 기상 연구원은 매일 양수·음수로 고도·수심·기온 변화를 계산합니다.',
    realAnswer: 1250,
    unit: 'm',
    tableData: [
      { x: 0, y: 3000 }, { x: 2, y: 2300 }, { x: 4, y: 1600 },
      { x: 6, y: 900 },  { x: 8, y: 200 },  { x: 9, y: -150 },
    ],
    quiz: [
      { q: '고도 +2,000m에서 +800m로 변할 때 변화량은?', a: '-1,200m', hint: '800 - 2000 = -1200 (하강)' },
      { q: '-300m와 +1,500m의 절댓값 합은?', a: '1,800', hint: '|-300| + |1500| = 300 + 1500' },
    ],
    jobs: ['파일럿', '항공 교통 관제사', '기상 연구원', '잠수함 승무원'],
  },
  {
    id: 'chef',
    emoji: '🍳',
    title: '요리사',
    category: '요리·식품',
    color: 'from-orange-400 to-amber-500',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    story: `중학교 1학년 민아(13살)는 미래의 셰프를 꿈꿉니다.
오늘은 온도 레시피를 공부하고 있어요.
"냉동고는 -18°C, 오븐은 +200°C… 얼마나 차이나지?"
요리사 선생님이 말씀하셨습니다: "온도도 정수야. 차이는 절댓값으로!"`,
    situation: '온도: 어는점(0°C) 기준. +200°C(오븐) ~ -18°C(냉동고).',
    question: '냉동고 -18°C에서 오븐 +200°C까지 온도 차이는? 절댓값으로 나타내면?',
    xLabel: '가열 시간(분)',
    yLabel: '온도(°C)',
    slope: 25,
    intercept: -18,
    slopeDesc: '분당 +25°C 상승 (양수 = 증가)',
    interceptDesc: '초기 온도 -18°C (냉동 상태)',
    equation: '온도 = 25t - 18',
    answer: '온도 차이: 200 - (-18) = 218°C\n절댓값: |200 - (-18)| = 218',
    whyIntegers: '온도의 음수·양수는 어는점 기준. 정수 덧셈으로 온도 변화를 쉽게 계산!',
    insight: '요리사, 식품 과학자, 냉장 물류 전문가는 매일 온도 범위를 정수로 계산합니다.',
    realAnswer: 218,
    unit: '°C',
    tableData: [
      { x: 0, y: -18 }, { x: 2, y: 32 }, { x: 4, y: 82 },
      { x: 6, y: 132 }, { x: 8, y: 182 }, { x: 9, y: 207 },
    ],
    quiz: [
      { q: '-18°C에서 +82°C가 되려면 몇 도 올라야 하나요?', a: '100°C', hint: '82 - (-18) = 82 + 18 = 100' },
      { q: '-5°C와 +30°C 중 절댓값이 더 큰 것은?', a: '+30°C (|-5|=5, |30|=30)', hint: '절댓값 = 수직선에서 0까지의 거리' },
    ],
    jobs: ['요리사·셰프', '식품 과학자', '냉장 물류 전문가', '식품 안전 검사원'],
  },
  {
    id: 'analyst',
    emoji: '📊',
    title: '금융 분석가',
    category: '금융·경제',
    color: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `중학교 1학년 준서(13살)는 미래의 금융 분석가를 꿈꿉니다.
주식 앱을 보다가 이상한 숫자들이 눈에 띄었어요.
"+3.5%, -2.1%, -7.8%…"
아버지가 설명해주셨습니다: "양수는 상승, 음수는 하락. 유리수로 주가를 분석하는 거야!"`,
    situation: '주가 변동: +면 상승, -면 하락. 유리수로 정확한 비율을 표현.',
    question: '주가가 월: +3.5%, 화: -2.1%, 수: -1.4% 변동 시 3일간 총 변동은?',
    xLabel: '날짜(일)',
    yLabel: '누적 수익률(%)',
    slope: -0.7,
    intercept: 3.5,
    slopeDesc: '일평균 -0.7% (음의 추세)',
    interceptDesc: '첫날 +3.5% 상승 (양수)',
    equation: '누적 = +3.5 + (-2.1) + (-1.4)',
    answer: '총 변동: 3.5 + (-2.1) + (-1.4) = 3.5 - 2.1 - 1.4 = 0%\n결과: 원점 복귀 (±0)',
    whyIntegers: '유리수의 덧셈으로 주가 누적 변동 계산! +와 -의 부호가 방향을 나타냅니다.',
    insight: '주식 분석가, 펀드 매니저, 회계사는 유리수 계산으로 손익을 매일 분석합니다.',
    realAnswer: 0,
    unit: '%',
    tableData: [
      { x: 0, y: 0 }, { x: 1, y: 3.5 }, { x: 2, y: 1.4 },
      { x: 3, y: 0 },  { x: 4, y: 1.2 }, { x: 5, y: -0.5 },
    ],
    quiz: [
      { q: '+5.2%와 -3.8%를 합산하면?', a: '+1.4%', hint: '5.2 + (-3.8) = 5.2 - 3.8 = 1.4' },
      { q: '절댓값 |-7.5|는?', a: '7.5', hint: '절댓값은 항상 0 이상' },
    ],
    jobs: ['주식 분석가', '펀드 매니저', '회계사', '경제 연구원'],
  },
  {
    id: 'weather',
    emoji: '🌡️',
    title: '기상 예보관',
    category: '과학·환경',
    color: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `중학교 1학년 수아(13살)는 기상 캐스터를 꿈꿉니다.
일기 예보 실습 중 이런 데이터를 받았어요.
"서울 -5°C, 제주 +8°C, 백두산 -22°C"
선생님이 설명하셨습니다: "정수로 표현하면 비교와 계산이 쉬워!"`,
    situation: '기온: 0°C 기준. 음수는 영하, 양수는 영상. 차이 = 절댓값 활용.',
    question: '백두산 -22°C와 제주도 +8°C의 기온 차이는? 서울 -5°C는 어느 쪽에 더 가깝나요?',
    xLabel: '시간(시)',
    yLabel: '기온(°C)',
    slope: 2,
    intercept: -22,
    slopeDesc: '시간당 +2°C 상승 (낮에 따뜻해짐)',
    interceptDesc: '새벽 기온 -22°C (최저)',
    equation: '기온 = 2t - 22',
    answer: '기온 차: 8 - (-22) = 30°C\n서울(-5): 백두산까지 |(-5)-(-22)|=17, 제주까지 |(-5)-8|=13 → 제주에 더 가까움',
    whyIntegers: '정수의 크기 비교와 절댓값으로 기온 차이를 한 눈에 파악!',
    insight: '기상 연구원, 환경 과학자, 기후 변화 분석가는 정수·유리수로 기온 데이터를 분석합니다.',
    realAnswer: 30,
    unit: '°C',
    tableData: [
      { x: 0, y: -22 }, { x: 3, y: -16 }, { x: 6, y: -10 },
      { x: 9, y: -4 },  { x: 12, y: 2 },  { x: 15, y: 8 },
    ],
    quiz: [
      { q: '-15°C와 +9°C 중 어느 쪽이 더 큰 수인가요?', a: '+9°C', hint: '수직선에서 오른쪽에 있는 수가 더 큰 수' },
      { q: '-15°C와 +9°C의 차이(절댓값)는?', a: '24°C', hint: '|9 - (-15)| = |9 + 15| = 24' },
    ],
    jobs: ['기상 예보관', '기후 변화 연구원', '환경 과학자', '극지 탐험가'],
  },
  {
    id: 'diver',
    emoji: '🤿',
    title: '해양 과학자',
    category: '해양·탐사',
    color: 'from-cyan-500 to-blue-600',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    badge: 'bg-cyan-100 text-cyan-700',
    story: `중학교 1학년 민준이는 해양 탐사 다큐멘터리를 보고 꿈을 정했습니다.
"해수면 0m, 산호초 -30m, 심해 -3,800m…"
깊이가 모두 음수인 이유가 궁금했어요.
"해수면을 기준(0)으로 아래는 음수, 위는 양수로 나타내는 거야!"`,
    situation: '수심: 해수면(0m) 기준. -30m=수심 30m, -3,800m=심해.',
    question: '수심 -30m 산호초에서 -3,800m 심해까지 거리는? 절댓값으로 표현하면?',
    xLabel: '잠수 시간(분)',
    yLabel: '수심(m)',
    slope: -50,
    intercept: 0,
    slopeDesc: '분당 -50m 잠수 (음수 = 더 깊이)',
    interceptDesc: '출발: 해수면 0m',
    equation: '수심 = -50t',
    answer: '거리: |-3800 - (-30)| = |-3770| = 3,770m\n두 수심 차이: 3,770m',
    whyIntegers: '해수면을 0으로 놓으면 수심이 자연스럽게 음수. 절댓값이 실제 깊이!',
    insight: '해양 과학자, 잠수함 설계사, 해저 케이블 엔지니어는 수심을 음수로 표현하고 절댓값으로 거리를 계산합니다.',
    realAnswer: 3770,
    unit: 'm',
    tableData: [
      { x: 0, y: 0 }, { x: 2, y: -100 }, { x: 4, y: -200 },
      { x: 6, y: -300 }, { x: 10, y: -500 }, { x: 20, y: -1000 },
    ],
    quiz: [
      { q: '수심 -200m와 -750m 중 해수면에 더 가까운 것은?', a: '-200m', hint: '절댓값이 작을수록 해수면에 가깝다' },
      { q: '-200m에서 -750m까지 거리는?', a: '550m', hint: '|-750 - (-200)| = |-550| = 550' },
    ],
    jobs: ['해양 과학자', '잠수함 설계사', '해저 케이블 엔지니어', '스쿠버 다이버'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '✈️',
    title: '항공·교통 전문가',
    example: '고도 계산, 상승/하강 속도 분석',
    formula: '고도 변화 = 착지고도 - 출발고도',
    salary: '연 5,000만~1억원+',
    color: 'from-blue-500 to-sky-600',
  },
  {
    icon: '📊',
    title: '금융·투자 분석가',
    example: '주가 등락률, 손익 계산, 포트폴리오 수익률',
    formula: '수익률 = (현재가 - 매수가) / 매수가 × 100',
    salary: '연 5,000만~2억원+',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: '🌡️',
    title: '기상·환경 과학자',
    example: '기온 변화 분석, 이상 기후 탐지',
    formula: '기온 변화 = 현재 기온 - 평균 기온',
    salary: '연 4,000만~8,000만원',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '🤿',
    title: '해양·극지 탐사 연구원',
    example: '수심 측정, 해저 지형 매핑',
    formula: '수심 = -(음파 왕복 시간 × 음속 / 2)',
    salary: '연 4,500만~8,500만원',
    color: 'from-cyan-500 to-blue-500',
  },
  {
    icon: '🏔️',
    title: '지질·측량 전문가',
    example: '해발 고도, 지하 깊이 측정',
    formula: '해발 = GPS 고도 - 지오이드 높이',
    salary: '연 4,000만~7,500만원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '🍳',
    title: '식품 과학·요리 전문가',
    example: '조리 온도 관리, 냉동·가열 범위 계산',
    formula: '온도 변화 = 목표 온도 - 현재 온도',
    salary: '연 3,500만~7,000만원',
    color: 'from-orange-500 to-red-400',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '🌡️',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '오전 기온 -8°C, 오후 기온 +5°C일 때 기온 변화는 몇 °C인가요?',
    hint: '기온 변화 = 오후 기온 - 오전 기온',
    answer: '+13°C 상승',
    explanation: '5 - (-8) = 5 + 8 = 13. 음수를 빼면 양수를 더하는 것과 같습니다!',
  },
  {
    id: 'c2',
    emoji: '📈',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '주가가 월 -3/4%, 화 +1/2%, 수 -1/4% 변동했습니다. 3일간 총 변동률은?',
    hint: '-3/4 + 1/2 + (-1/4) = -3/4 + 2/4 + (-1/4) = ?',
    answer: '-2/4 = -1/2%',
    explanation: '-3/4 + 2/4 - 1/4 = -2/4 = -1/2%. 유리수 덧셈은 통분 후 계산!',
  },
  {
    id: 'c3',
    emoji: '✈️',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '비행기가 고도 +8,500m에서 분당 -350m씩 하강합니다. 10분 후 고도는? 착륙(고도 0)까지 몇 분이 걸리나요?',
    hint: '고도 = 8500 + (-350) × 시간. 고도 = 0일 때 시간을 구하세요.',
    answer: '10분 후: +5,000m / 착륙: 약 24.3분',
    explanation: '10분: 8500 + (-3500) = 5000m. 착륙: 8500 ÷ 350 ≈ 24.3분',
  },
  {
    id: 'c4',
    emoji: '🤿',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '잠수함이 -50m에서 출발해 분당 -30m씩 잠수합니다. -500m에 도달하는 데 몇 분이 걸리나요?',
    hint: '-50 + (-30) × t = -500 에서 t를 구하세요.',
    answer: '15분',
    explanation: '-50 + (-30t) = -500 → -30t = -450 → t = 15분',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '항공·교통': 'bg-blue-100 text-blue-700',
  '요리·식품': 'bg-orange-100 text-orange-700',
  '금융·경제': 'bg-emerald-100 text-emerald-700',
  '과학·환경': 'bg-violet-100 text-violet-700',
  '해양·탐사': 'bg-cyan-100 text-cyan-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'forward' | 'inverse'

interface CalcResult {
  mode: 'forward' | 'inverse'
  inputVal: number
  outputVal: number
  steps: string[]
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('forward')
  const [inputVal, setInputVal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setInputVal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const v = parseFloat(inputVal)
    if (isNaN(v)) return

    if (mode === 'forward') {
      const raw = scenario.slope * v + scenario.intercept
      const y = Math.round(raw * 100) / 100
      setResult({
        mode: 'forward',
        inputVal: v,
        outputVal: y,
        steps: [
          `공식: ${scenario.equation}`,
          `t = ${v} 대입`,
          `= ${scenario.slope} × ${v} + (${scenario.intercept})`,
          `= ${Math.round(scenario.slope * v * 100) / 100} + (${scenario.intercept})`,
          `= ${y.toLocaleString()} ${scenario.unit}`,
        ],
      })
    } else {
      if (scenario.slope === 0) return
      const raw = (v - scenario.intercept) / scenario.slope
      const x = Math.round(raw * 100) / 100
      setResult({
        mode: 'inverse',
        inputVal: v,
        outputVal: x,
        steps: [
          `공식: ${scenario.equation}`,
          `${scenario.yLabel.split('(')[0]} = ${v} 설정`,
          `${v} = ${scenario.slope} × t + (${scenario.intercept})`,
          `${scenario.slope} × t = ${v} − (${scenario.intercept}) = ${v - scenario.intercept}`,
          `t = ${v - scenario.intercept} ÷ ${scenario.slope} = ${x}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.02 + 1
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  const xLabelShort = scenario.xLabel.split('(')[0]
  const yLabelShort = scenario.yLabel.split('(')[0]

  return (
    <div className="card border-2 border-primary-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-primary-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('forward')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'forward' ? 'bg-primary-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            {xLabelShort} → {yLabelShort}
          </button>
          <button
            onClick={() => handleModeChange('inverse')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'inverse' ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            {yLabelShort} → {xLabelShort}
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">식</span>
        <span className="font-mono font-bold text-primary-700 text-lg">{scenario.equation}</span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'forward'
              ? `🔢 ${scenario.xLabel} 를 입력하세요`
              : `🔢 ${scenario.yLabel} 를 입력하세요`}
          </label>
          <input
            type="number"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="숫자를 입력하고 계산!"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!inputVal}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'forward'
                ? 'bg-primary-600 hover:bg-primary-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-purple-600 hover:bg-purple-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3">📝 풀이 과정 (단계별)</p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-emerald-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-emerald-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'forward' ? 'bg-primary-50 border-primary-300' : 'bg-purple-50 border-purple-300'
          }`}>
            <p className="text-xs font-semibold text-slate-500 mb-1">
              {mode === 'forward'
                ? `${scenario.xLabel} = ${result.inputVal} 일 때`
                : `${scenario.yLabel} = ${result.inputVal.toLocaleString()} ${scenario.unit} 일 때`}
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'forward' ? 'text-primary-700' : 'text-purple-700'}`}>
                {result.outputVal.toLocaleString()}
              </span>
              <span className="text-base font-semibold text-slate-500">
                {mode === 'forward' ? scenario.unit : '분/시간'}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기 — 직접 계산한 후 입력해보세요!</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder={`내 답: __ ${mode === 'forward' ? scenario.unit : ''}`}
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉 완벽합니다!</>
                  : <><XCircle size={16} /> 아쉬워요. 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>

          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-emerald-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 ───────────────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{ch.emoji}</span>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-emerald-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-emerald-600 leading-relaxed">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function N1_2RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 정수·유리수', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 정수·유리수</h1>
            <p className="text-sm text-slate-500">파일럿, 요리사, 금융 분석가, 기상 예보관, 해양 과학자 — 모두 정수와 유리수를 씁니다!</p>
          </div>
        </div>

        {/* Career Story Banner */}
        <div className="mt-4 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 정수·유리수를 만나다!</p>
          <p className="text-sm opacity-90 mb-3">양수·음수가 세상 어디서 어떻게 쓰이는지 직업 스토리로 만나보세요!</p>
          <div className="grid grid-cols-5 gap-2">
            {[
              { icon: '✈️', job: '파일럿' },
              { icon: '🍳', job: '요리사' },
              { icon: '📊', job: '금융 분석가' },
              { icon: '🌡️', job: '기상 예보관' },
              { icon: '🤿', job: '해양 과학자' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          {/* Category Filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Scenario Cards — 좌측 1열 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category] ?? 'bg-slate-100 text-slate-600'}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-primary-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail Panel — 우측 2열 */}
            <div className="lg:col-span-2 space-y-4">
              {/* Situation Card */}
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category] ?? 'bg-slate-100 text-slate-600'}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                {/* Story */}
                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 정수·유리수</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                    {selected.story}
                  </p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                {/* Equation Breakdown */}
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <span className="text-2xl font-black text-primary-700 font-mono">{selected.equation}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold">변화율 (계수 = {selected.slope})</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold">초기값 (= {selected.intercept})</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Question & Answer */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-emerald-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-amber-700">
                    <span className="font-semibold">왜 정수·유리수인가?</span> {selected.whyIntegers}
                  </p>
                </div>
              </div>

              {/* Table */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-primary-500" />
                    표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>

                {showTable ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">
                            {selected.xLabel}
                          </th>
                          <th className="border border-slate-200 px-3 py-2 bg-primary-50 text-primary-700 text-left">
                            {selected.yLabel}
                          </th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">
                            변화량 (Δy)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className={`border border-slate-200 px-3 py-2 font-mono font-bold ${row.y < 0 ? 'text-red-600' : 'text-primary-700'}`}>
                              {row.y.toLocaleString()}
                            </td>
                            <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500">
                              {i === 0 ? '—' : (
                                <span className={`font-mono ${row.y - selected.tableData[i - 1].y < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                                  {row.y - selected.tableData[i - 1].y > 0 ? '+' : ''}
                                  {(row.y - selected.tableData[i - 1].y).toFixed(1)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      음수(빨간색) = 감소·하강·영하 / 양수(파란색) = 증가·상승·영상
                    </p>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 양수·음수 변화를 확인해보세요!</p>
                  </div>
                )}
              </div>

              {/* Calculator */}
              <ScenarioCalculator scenario={selected} />

              {/* Quiz */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              {/* Jobs related */}
              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-primary-500" />
                  이 상황에서 정수·유리수를 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-primary-100 text-primary-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              {/* Insight */}
              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['양수·음수', '절댓값', '수의 대소', '유리수 계산'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Summary */}
          <div className="mt-8 card bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 정수·유리수 실생활 공통 패턴</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '⊕⊖', title: '부호 = 방향', desc: '양수(+)는 위·오른쪽·상승, 음수(-)는 아래·왼쪽·하강. 부호가 방향을 나타낸다!', color: 'text-blue-600' },
                { icon: '|x|', title: '절댓값 = 실제 크기', desc: '부호를 제거한 순수한 크기. 고도·온도·수심의 "얼마나 멀리?"를 표현!', color: 'text-orange-600' },
                { icon: '±', title: '정수 연산 = 현실 계산', desc: '음수끼리 더하기, 음수 빼기 등이 실제 세계에서 매일 쓰이는 계산!', color: 'text-emerald-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2 font-black text-slate-700">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 정수·유리수를 사용하는 직업들</h2>
            <p className="text-sm text-blue-100">
              양수·음수·유리수는 전 세계 수천만 직업인이 매일 활용하는 핵심 도구입니다.
              지금 배우는 것이 미래 직업의 기반이 됩니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-primary-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* AI & 미래 직업 섹션 */}
          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 정수·유리수가 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: 'AI 모델의 가중치는 유리수',
                  desc: '딥러닝의 가중치(weight)는 -2.3, +0.7 같은 유리수. 정수·유리수가 AI의 핵심!',
                  color: 'border-yellow-400',
                },
                {
                  title: '센서 데이터는 모두 유리수',
                  desc: '온도 -3.7°C, 고도 +1234.5m — 스마트폰, 드론, 자율주행차의 모든 센서값이 유리수!',
                  color: 'border-blue-400',
                },
                {
                  title: '금융 AI의 기초',
                  desc: '주가 예측 모델, 리스크 계산 모두 양수·음수 유리수 계산에서 출발합니다.',
                  color: 'border-emerald-400',
                },
                {
                  title: '좌표계와 3D 공간',
                  desc: '게임, VR, 메타버스의 3D 좌표는 모두 정수·유리수. x=-3.5, y=+7.2처럼!',
                  color: 'border-purple-400',
                },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 학습 경로 */}
          <div className="card border border-primary-200 bg-primary-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-primary-600" /> 정수·유리수에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '정수·유리수', active: true },
                { step: '중1', topic: '일차방정식', active: false },
                { step: '중2', topic: '유리수·무리수', active: false },
                { step: '중3', topic: '실수', active: false },
                { step: '고1', topic: '복소수', active: false },
                { step: '대학', topic: '수치해석', active: false },
                { step: '직업', topic: 'AI·데이터사이언스', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-primary-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-amber-100">
              실제 상황에서 정수·유리수를 직접 계산해보세요.
              양수·음수의 부호가 핵심입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          {/* 핵심 비법 */}
          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-amber-500" /> 정수·유리수 문제 풀기 비법
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  step: '① 기준점 찾기',
                  desc: '"0이 어디인가?" → 해수면 0m, 0°C, 잔액 0원',
                  example: '해수면 기준: 위=양수, 아래=음수',
                  color: 'bg-blue-50 border-blue-200 text-blue-800',
                },
                {
                  step: '② 부호 결정',
                  desc: '"증가·상승·이득=+, 감소·하강·손실=-"',
                  example: '-5°C → 영하 5도 (0보다 5 작음)',
                  color: 'bg-orange-50 border-orange-200 text-orange-800',
                },
                {
                  step: '③ 절댓값 계산',
                  desc: '"실제 거리·크기가 필요할 때 → 절댓값!"',
                  example: '|-18 - 5| = |-23| = 23 (온도 차)',
                  color: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-primary-50 border border-primary-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-primary-700">기준 설정 → 부호 결정 → 정수 계산 → 답!</p>
              <p className="text-xs text-primary-500 mt-1">양수와 음수로 방향까지 표현하는 것이 정수의 힘!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
